import { proxyCustomElement, HTMLElement, h, Fragment } from '@stencil/core/internal/client';
import { g as getColorFromString } from './colors.js';
import { l as luxon } from './luxon.js';
import { d as defineCustomElement$2 } from './tooltip.js';

const participantTimelineTrackCss = ":host{display:contents}.track{position:absolute;border-radius:0.125rem;text-wrap:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:2px;font:var(--tt-font-medium);color:white;height:1rem}.track:hover{outline:1px solid black;z-index:2;outline-offset:-1px}.track .track-name{width:auto;display:block;overflow:hidden;margin-inline:0.25rem;text-overflow:ellipsis}.track .tooltip-trigger{width:100%;height:100%}";

const ParticipantTimelineTrack = /*@__PURE__*/ proxyCustomElement(class extends HTMLElement {
  constructor() {
    super();
    this.__registerHost();
    this.__attachShadow();
    this.offset = [0, 1];
    this.boundaries = [0, 1];
    this.name = undefined;
    this.identity = undefined;
    this.start = undefined;
    this.end = undefined;
    this.sid = undefined;
    this.duration = undefined;
  }
  getIdentityInfoInternal() {
    const [role, joinTime, name, id] = this.identity.split('|');
    return { role, joinTime, name: name || 'headless', id: id || 'headless' };
  }
  /** Get deconstructed info from the participant identity (role, joinTime, name and id) */
  async getIdentityInfo() {
    return this.getIdentityInfoInternal();
  }
  /** Force component to recalculate positioning of the track. */
  async update() {
    this.recalculateOffset();
  }
  /** Get start and end timestamps of the track based on the `start` and `end` props */
  async getTime() {
    return this.boundaries;
  }
  parseBoundaries() {
    const timezone = this.getTimezone();
    const startDate = !Number.isNaN(+this.start)
      ? +this.start
      : luxon.DateTime.fromJSDate(new Date(this.start), { zone: timezone }).toMillis();
    let endDate = !Number.isNaN(+this.end)
      ? +this.end
      : luxon.DateTime.fromJSDate(new Date(this.end), { zone: timezone }).toMillis();
    if (Number.isNaN(endDate))
      endDate = Date.now();
    this.boundaries = [startDate, endDate];
  }
  updateSiblings() {
    var _a;
    (_a = this.element.closest('tt-participant-timeline')) === null || _a === void 0 ? void 0 : _a.updateAll();
  }
  async recalculateOffset() {
    const timeline = this.element.closest('tt-participant-timeline');
    if (!timeline)
      return;
    const offset = await timeline.getRelativeOffset(...(await this.getTime()));
    this.offset = [offset[0], offset[1]];
  }
  getTimezone() {
    var _a, _b;
    return (_b = (_a = this.element.closest('tt-participant-timeline')) === null || _a === void 0 ? void 0 : _a.timezone) !== null && _b !== void 0 ? _b : 'UTC';
  }
  connectedCallback() {
    this.getIdentityInfoInternal = this.getIdentityInfoInternal.bind(this);
    this.getIdentityInfo = this.getIdentityInfo.bind(this);
    this.update = this.update.bind(this);
    this.getTime = this.getTime.bind(this);
    this.updateSiblings = this.updateSiblings.bind(this);
    this.parseBoundaries = this.parseBoundaries.bind(this);
    this.recalculateOffset = this.recalculateOffset.bind(this);
    this.getTimezone = this.getTimezone.bind(this);
    this.parseBoundaries();
  }
  render() {
    return (h("div", { class: "track", title: this.identity, style: {
        'left': `${this.offset[0] * 100}%`,
        'right': `${(1 - this.offset[1]) * 100}%`,
        'background-color': getColorFromString(this.name)[1],
      } }, h("tt-tooltip", { placement: "top-start", hoist: true }, h("div", { class: "tooltip-trigger" }, this.offset[1] - this.offset[0] > 0.1 && h("span", { class: "track-name" }, this.name)), h("div", { class: "tooltip-content", slot: "content" }, h("div", null, h("b", null, this.name), " \u2014 ", this.getIdentityInfoInternal().role, this.duration && h(Fragment, null, " (", this.duration, ")")), h("div", null, luxon.DateTime.fromMillis(this.boundaries[0]).setZone(this.getTimezone()).toFormat('yyyy-MM-dd HH:mm:ss'), ' → ', luxon.DateTime.fromMillis(this.boundaries[1]).setZone(this.getTimezone()).toFormat('yyyy-MM-dd HH:mm:ss')), this.sid && h("div", null, this.sid)))));
  }
  get element() { return this; }
  static get watchers() { return {
    "start": ["parseBoundaries"],
    "end": ["parseBoundaries"],
    "identity": ["updateSiblings"],
    "boundaries": ["updateSiblings"]
  }; }
  static get style() { return participantTimelineTrackCss; }
}, [1, "tt-participant-timeline-track", {
    "name": [1],
    "identity": [1],
    "start": [8],
    "end": [8],
    "sid": [1],
    "duration": [1],
    "offset": [32],
    "boundaries": [32],
    "getIdentityInfo": [64],
    "update": [64],
    "getTime": [64]
  }]);
function defineCustomElement$1() {
  if (typeof customElements === "undefined") {
    return;
  }
  const components = ["tt-participant-timeline-track", "tt-tooltip"];
  components.forEach(tagName => { switch (tagName) {
    case "tt-participant-timeline-track":
      if (!customElements.get(tagName)) {
        customElements.define(tagName, ParticipantTimelineTrack);
      }
      break;
    case "tt-tooltip":
      if (!customElements.get(tagName)) {
        defineCustomElement$2();
      }
      break;
  } });
}

const TtParticipantTimelineTrack = ParticipantTimelineTrack;
const defineCustomElement = defineCustomElement$1;

export { TtParticipantTimelineTrack, defineCustomElement };
